import React, { useState, useEffect } from "react";
import AdminSidebar from "../../../components/AdminSidebar";
import { axiosInstance } from "../../../utils/axios";
import { Combobox } from "@headlessui/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import excelIcon from "../../../assets/excel-icon.webp";
import useDialogStore from "../../../stores/useDialogStore";
import ColumnSelector from "../../../components/ColumnSelector";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/20/solid";
import NoData from "../../../components/NoData";
import LoadingComponent from "../../../components/LoadingComponent";
import dayjs from "dayjs";
import EditableCell from "../../../components/EditableCell";
import EditableSelectCell from "../../../components/EditableSelectCell";
import EditableOptionsCell from "../../../components/EditableOptionsCell";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import ColumnSearch from "../../../components/ColumnSearch";
import Button from "../../../components/Button";
import PaginationV2 from "../../../components/PaginationV2";
import { getUser } from "../../../utils/storage";
import EditSerialNumberCell from "../../../components/EditSerialNumberCell";

const ClientReport = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState({
    unique_id: true,
    brand: true,
    model: true,
    serial_no: true,
    counter_black: true,
    counter_color: true,
    total_counter: true,
    palette: true,
    created_at: true,
    options: true,
    comment: true,
    invoice_number: true,
    status: true,
    reserved_date: true,
    sold_date: true,
  });

  const columnOptions = [
    { key: "unique_id", label: "Unique ID" },
    { key: "brand", label: "Brand" },
    { key: "model", label: "Model" },
    { key: "serial_no", label: "Serial No" },
    { key: "counter_black", label: "Counter Black" },
    { key: "counter_color", label: "Counter Color" },
    { key: "total_counter", label: "Total Counter" },
    { key: "palette", label: "Palette" },
    { key: "created_at", label: "Date Registered" },
    { key: "options", label: "Device Options" },
    { key: "comment", label: "Comment" },
    { key: "invoice_number", label: "Invoice Number" },
    { key: "status", label: "Status" },
    { key: "reserved_date", label: "Reserved Date" },
    { key: "sold_date", label: "Sold Date" },
  ];
  const user = getUser();
  const { openDialog, closeDialog } = useDialogStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const sortConfig = {
    key: null,
    direction: "desc",
  };
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleColumnSearch = (columnKey, query) => {
    const params = new URLSearchParams(location.search);
    if (query) {
      params.set(columnKey, query);
    } else {
      params.delete(columnKey);
    }
    params.delete("page"); // Reset to first page when searching
    navigate(`${location.pathname}?${params.toString()}`);
    queryClient.invalidateQueries(["clientInfo"]);
  };

  const handleColumnSort = (column, direction) => {
    const params = new URLSearchParams(location.search);
    const currentSortBy = params.get("sortBy");
    const currentOrder = params.get("order");

    // If clicking the same column, toggle direction
    if (currentSortBy === column) {
      const newDirection = currentOrder === "asc" ? "desc" : "asc";
      params.set("order", newDirection);
    } else {
      // If clicking a new column, set it to ascending
      params.set("sortBy", column);
      params.set("order", "desc");
    }

    navigate(`${location.pathname}?${params.toString()}`);
    queryClient.invalidateQueries(["clientInfo"]);
  };

  const handleClearFilters = () => {
    navigate(location.pathname);
    queryClient.invalidateQueries(["clientInfo"]);
  };

  // Fetch clients using React Query
  const { data: clientsData, isLoading: clientsLoading } = useQuery({
    queryKey: ["clients", searchTerm],
    queryFn: async () => {
      if (!searchTerm.trim()) return [];
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/clients?search=${searchTerm.trim()}`
      );
      return response.data.data.data;
    },
    enabled: searchTerm.trim().length > 0,
  });
  console.log(clientsData);
  const {
    data: clientInfo,
    isLoading: clientInfoLoading,
    refetch,
  } = useQuery({
    queryKey: ["clientInfo", selectedClientId, location.search],
    queryFn: async () => {
      if (!selectedClientId) return null;
      const params = new URLSearchParams(location.search);
      params.set("client_id", selectedClientId);
      const response = await axiosInstance.get(
        `${
          window.REACT_APP_API_ENDPOINT
        }/device-statuses/client-info/get?${params.toString()}`
      );
      return response.data;
    },
    enabled: !!selectedClientId,
    refetchOnWindowFocus: false,
    staleTime: 0,
    cacheTime: 0,
  });
  console.log(clientInfo);

  // Export mutation
  const exportMutation = useMutation({
    mutationFn: async (selectedColumnss) => {
      const params = new URLSearchParams(location.search);
      const response = await axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/export-excel/client-report`,
        {
          client_id: selectedClientId,
          columns: Object.entries(selectedColumnss)
            .filter(([_, selected]) => selected)
            .map(([key]) => key),
          params: params.toString(),
        },
        {
          responseType: "blob", // This tells Axios to expect a file
        }
      );
      return response;
    },
    onSuccess: (response) => {
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `client_report_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url); // Clean up
      closeDialog();
      toast.success("Report exported successfully!");
    },
    onError: (error) => {
      console.error("Export error:", error);
      toast.error("Failed to export report. Please try again.");
    },
  });

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setSelectedClientId(null);
    // Clear URL parameters when clearing search
    navigate(location.pathname);
    queryClient.invalidateQueries(["clientInfo"]);
  };

  const handleExport = (selectedColumnss) => {
    if (Object.values(selectedColumnss).some((selected) => selected)) {
      exportMutation.mutate(selectedColumnss);
    } else {
      toast.warning("Please select at least one column to export");
    }
  };

  const ColumnSelectorContent = () => {
    const [localSelectedColumns, setLocalSelectedColumns] =
      useState(selectedColumns);

    return (
      <div className="bg-white rounded-lg w-full p-4">
        <ColumnSelector
          columns={columnOptions}
          selectedColumns={localSelectedColumns}
          setSelectedColumns={setLocalSelectedColumns}
        />
        <div className="mt-6 flex justify-end space-x-2">
          <Button variant="light" size="sm" onClick={() => closeDialog()}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              if (
                Object.values(localSelectedColumns).some((selected) => selected)
              ) {
                setSelectedColumns(localSelectedColumns);
                handleExport(localSelectedColumns);
                closeDialog();
              } else {
                toast.warning("Please select at least one column to export");
              }
            }}
          >
            Export
          </Button>
        </div>
      </div>
    );
  };

  const handleOpenColumnSelector = () => {
    openDialog({
      title: "Select Columns to Export",
      content: <ColumnSelectorContent />,
    });
  };

  const editCellMutation = useMutation({
    mutationFn: ({ deviceId, field, value }) =>
      axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/devices/patch/${deviceId}`,
        { [field]: value, user_id: user.id }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(["clientInfo"]);
      toast.success("Device updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update device. Please try again.");
      console.error("Update device error:", error);
    },
  });
  const editDeviceStatusMutation = useMutation({
    mutationFn: ({ deviceId, field, value }) =>
      axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/device-statuses/patch/${deviceId}`,
        { [field]: value, user_id: user.id }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(["sold-devices"]);
      toast.success("Device Status updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update device status. Please try again.");
      console.error("Update device status error:", error);
    },
  });
  const brands = useQuery({
    queryKey: ["brands"],
    queryFn: () =>
      axiosInstance.get(`${window.REACT_APP_API_ENDPOINT}/device-brands`),
    select: (data) => data.data.data.map(({ id, name }) => ({ id, name })),
  }).data;

  const models = useQuery({
    queryKey: ["models"],
    queryFn: () =>
      axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-models?type=all`
      ),
    select: (data) =>
      data.data.data.data.map(({ id, name, device_brand_id }) => ({
        id,
        name,
        device_brand_id,
      })),
  }).data;

  console.log(clientInfo?.data);

  return (
    <AdminSidebar>
      <div className="space-y-6">
        <div className="sm:flex sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
              Client Report
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              View and analyze client-related data and statistics
            </p>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="space-y-4">
            <div className="max-w-sm">
              <label
                htmlFor="client-search"
                className="block text-sm font-medium text-gray-700"
              >
                Search and Select Client
              </label>
              <div className="relative">
                <Combobox
                  value={selectedClientId}
                  onChange={(clientId) => {
                    const selectedClient = clientsData?.find(
                      (c) => c.id === clientId
                    );
                    if (selectedClient) {
                      setSelectedClientId(selectedClient.id);
                      setSearchTerm(selectedClient.name);
                      // Update URL with client_id parameter
                      const params = new URLSearchParams(location.search);
                      params.set("client_id", selectedClient.id);
                      navigate(`${location.pathname}?${params.toString()}`);
                    }
                  }}
                >
                  <div className="relative">
                    <Combobox.Input
                      id="client-search"
                      className="w-full border border-gray-300 rounded-md p-2 mt-1 mb-4 pr-20"
                      placeholder="Search by client name"
                      value={searchTerm}
                      onChange={(event) =>
                        handleSearchChange(event.target.value)
                      }
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 mt-1 mb-4">
                      {searchTerm && (
                        <button
                          type="button"
                          onClick={handleClearSearch}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>
                  <Combobox.Options className="absolute w-full border border-gray-300 rounded-md p-2 mt-1 bg-white shadow-lg z-10">
                    {clientsLoading ? (
                      <div className="text-gray-500 text-center py-2">
                        Searching...
                      </div>
                    ) : clientsData?.length === 0 ? (
                      <div className="text-gray-500 text-center py-2">
                        <NoData message="No clients found" />
                      </div>
                    ) : (
                      clientsData?.map((client) => (
                        <Combobox.Option
                          key={client.id}
                          value={client.id}
                          className={({ active }) =>
                            `cursor-pointer select-none relative py-2 pl-3 pr-9 ${
                              active
                                ? "bg-blue-600 text-white"
                                : "text-gray-900"
                            }`
                          }
                        >
                          {({ selected, active }) => (
                            <>
                              <span
                                className={`block truncate ${
                                  selected ? "font-semibold" : "font-normal"
                                }`}
                              >
                                {client.name}
                              </span>
                              {selected && (
                                <span
                                  className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                                    active ? "text-white" : "text-blue-600"
                                  }`}
                                >
                                  <svg
                                    className="w-5 h-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 6.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l7-7a1 1 0 000-1.414z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </span>
                              )}
                            </>
                          )}
                        </Combobox.Option>
                      ))
                    )}
                  </Combobox.Options>
                </Combobox>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="space-y-4">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
              Client Report
            </h2>
            {clientInfoLoading ? (
              <LoadingComponent />
            ) : clientInfo ? (
              <div className="mt-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {clientInfo.client_name.toUpperCase()}'s Devices
                  </h3>
                  <div className="flex space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<XMarkIcon className="h-4 w-4" />}
                      onClick={handleClearFilters}
                      className="mb-2 lg:mb-0"
                    >
                      Clear Filters
                    </Button>
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={handleOpenColumnSelector}
                      className="mb-2 lg:mb-0"
                      icon={excelIcon}
                    >
                      Download Full Client Report
                    </Button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="lg:min-w-full divide-y divide-gray-300">
                    <thead className="bg-black">
                      <tr>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          <div className="flex items-center gap-2">
                            <ColumnSearch
                              columnName="Unique ID"
                              columnKey="unique_id"
                              onSearch={handleColumnSearch}
                              onSort={handleColumnSort}
                              sortConfig={sortConfig}
                              placeholder="Search by Unique ID..."
                            />
                          </div>
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          <div className="flex items-center gap-2">
                            <ColumnSearch
                              columnName="Brand"
                              columnKey="brand"
                              onSearch={handleColumnSearch}
                              onSort={handleColumnSort}
                              sortConfig={sortConfig}
                              placeholder="Search by Brand..."
                            />
                          </div>
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          <div className="flex items-center gap-2">
                            <ColumnSearch
                              columnName="Model"
                              columnKey="model"
                              onSearch={handleColumnSearch}
                              onSort={handleColumnSort}
                              sortConfig={sortConfig}
                              placeholder="Search by Model..."
                            />
                          </div>
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          <div className="flex items-center gap-2">
                            <ColumnSearch
                              columnName="Serial"
                              columnKey="serial_no"
                              onSearch={handleColumnSearch}
                              onSort={handleColumnSort}
                              sortConfig={sortConfig}
                              placeholder="Search by Serial..."
                            />
                          </div>
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          <div className="flex items-center gap-2">
                            <ColumnSearch
                              columnName="Counter Black"
                              columnKey="counter_black"
                              onSearch={handleColumnSearch}
                              onSort={handleColumnSort}
                              sortConfig={sortConfig}
                              placeholder="Search by Counter Black..."
                            />
                          </div>
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          <div className="flex items-center gap-2">
                            <ColumnSearch
                              columnName="Counter Color"
                              columnKey="counter_color"
                              onSearch={handleColumnSearch}
                              onSort={handleColumnSort}
                              sortConfig={sortConfig}
                              placeholder="Search by Counter Color..."
                            />
                          </div>
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          <div className="flex items-center gap-2">
                            <ColumnSearch
                              columnName="Total Counter"
                              columnKey="total_counter"
                              onSearch={handleColumnSearch}
                              onSort={handleColumnSort}
                              sortConfig={sortConfig}
                              placeholder="Search by Total Counter..."
                            />
                          </div>
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          <div className="flex items-center gap-2">
                            <ColumnSearch
                              columnName="Palette"
                              columnKey="palette"
                              onSearch={handleColumnSearch}
                              onSort={handleColumnSort}
                              sortConfig={sortConfig}
                              placeholder="Search by Palette..."
                            />
                          </div>
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          <div className="flex items-center gap-2">
                            <ColumnSearch
                              columnName="Date Registered"
                              columnKey="created_at"
                              onSearch={handleColumnSearch}
                              onSort={handleColumnSort}
                              sortConfig={sortConfig}
                              isDate={true}
                              placeholder="Search by Date..."
                            />
                          </div>
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          Options
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          Comment
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          Status
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          <div className="flex items-center gap-2">
                            <ColumnSearch
                              columnName="Reserved Date"
                              columnKey="reserved_date"
                              onSearch={handleColumnSearch}
                              onSort={handleColumnSort}
                              sortConfig={sortConfig}
                              isDate={true}
                              placeholder="Search by Reserved Date..."
                            />
                          </div>
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          <div className="flex items-center gap-2">
                            <ColumnSearch
                              columnName="Sold Date"
                              columnKey="sold_date"
                              onSearch={handleColumnSearch}
                              onSort={handleColumnSort}
                              sortConfig={sortConfig}
                              isDate={true}
                              placeholder="Search by Sold Date..."
                            />
                          </div>
                        </th>
                        <th className="px-4 py-4 text-left text-white text-sm">
                          <div className="flex items-center gap-2">
                            <ColumnSearch
                              columnName="Invoice Number"
                              columnKey="invoice_number"
                              onSearch={handleColumnSearch}
                              onSort={handleColumnSort}
                              sortConfig={sortConfig}
                              placeholder="Search by Invoice Number..."
                            />
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {clientInfoLoading ? (
                        <tr>
                          <td colSpan={13} className="text-center">
                            <LoadingComponent />
                          </td>
                        </tr>
                      ) : clientInfo?.data?.length === 0 ? (
                        <tr>
                          <td colSpan={13} className="text-center">
                            <NoData message="No devices found for this client." />
                          </td>
                        </tr>
                      ) : (
                        clientInfo?.data?.map((device, index) => (
                          <tr
                            key={device.id}
                            className={`divide-y divide-gray-200 ${
                              index % 2 === 0 ? "bg-white" : "bg-gray-100"
                            }`}
                          >
                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              <div className="mt-1 text-black">
                                {device.unique_id}
                              </div>
                            </td>
                            <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                              <div className="flex items-center pl-4">
                                {brands && (
                                  <EditableSelectCell
                                    value={device.device_brand_id}
                                    displayValue={device.brand}
                                    id={device.id}
                                    options={brands}
                                    field="brand"
                                    onSave={(data) => {
                                      editCellMutation.mutate({
                                        deviceId: data.id,
                                        field: "device_brand_id",
                                        value: data.value,
                                      });
                                    }}
                                  />
                                )}
                              </div>
                            </td>
                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              {models && (
                                <EditableSelectCell
                                  value={device.device_model_id}
                                  displayValue={device.model}
                                  id={device.id}
                                  options={models.filter(
                                    (model) =>
                                      model.device_brand_id ===
                                      device.device_brand_id
                                  )}
                                  field="device_model_id"
                                  onSave={(data) =>
                                    editCellMutation.mutate({
                                      deviceId: data.id,
                                      field: "device_model_id",
                                      value: data.value,
                                    })
                                  }
                                />
                              )}
                            </td>
                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              <EditSerialNumberCell
                                value={device.serial_no || "—"}
                                displayValue={device.serial_no || "—"}
                                id={device.id}
                                field="serial_no"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    deviceId: data.id,
                                    field: "serial_no",
                                    value: data.value,
                                  })
                                }
                              />
                            </td>
                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              <EditableCell
                                value={device.counter_black || "—"}
                                displayValue={device.counter_black || "—"}
                                id={device.id}
                                field="counter_black"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    deviceId: data.id,
                                    field: "counter_black",
                                    value: data.value,
                                  })
                                }
                              />
                            </td>
                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              <EditableCell
                                value={device.counter_color || "—"}
                                displayValue={device.counter_color || "—"}
                                id={device.id}
                                field="counter_color"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    deviceId: data.id,
                                    field: "counter_color",
                                    value: data.value,
                                  })
                                }
                              />
                            </td>
                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              <EditableCell
                                value={device.total_counter || "—"}
                                displayValue={device.total_counter || "—"}
                                id={device.id}
                                field="total_counter"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    deviceId: data.id,
                                    field: "total_counter",
                                    value: data.value,
                                  })
                                }
                              />
                            </td>
                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              <EditableCell
                                value={device.palette || "—"}
                                displayValue={device.palette || "—"}
                                id={device.id}
                                field="palette"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    deviceId: data.id,
                                    field: "palette",
                                    value: data.value,
                                  })
                                }
                              />
                            </td>
                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              {dayjs
                                .utc(device.created_at)
                                .format("DD-MM-YYYY")}
                            </td>

                            <EditableOptionsCell
                              deviceId={device.id}
                              deviceOptions={device.device_options}
                            />
                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              <EditableCell
                                value={device.comment || ""}
                                displayValue={device.comment || ""}
                                id={device.id}
                                field="comment"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    deviceId: data.id,
                                    field: "comment",
                                    value: data.value,
                                  })
                                }
                              />
                            </td>
                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              <span
                                className={`whitespace-nowrap uppercase p-3 text-sm text-black ${
                                  device?.status === "Sold"
                                    ? "text-green-500 bg-green-50 rounded-md px-2 py-1"
                                    : "text-orange-500 bg-orange-50 rounded-md px-2 py-1"
                                }`}
                              >
                                {device?.status}
                              </span>
                            </td>
                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              {device?.reserved_date
                                ? dayjs
                                    .utc(device.reserved_date)
                                    .format("DD-MM-YYYY")
                                : "—"}
                            </td>
                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              {device?.sold_date
                                ? dayjs
                                    .utc(device.sold_date)
                                    .format("DD-MM-YYYY")
                                : "—"}
                            </td>
                            <td className="whitespace-nowrap p-3 text-sm text-black">
                              <EditableCell
                                value={device.invoice_number || ""}
                                displayValue={device.invoice_number || ""}
                                id={device.id}
                                field="invoice_number"
                                onSave={(data) =>
                                  editDeviceStatusMutation.mutate({
                                    deviceId: data.id,
                                    field: "invoice_number",
                                    value: data.value,
                                  })
                                }
                              />
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <PaginationV2
                  current_page={clientInfo?.current_page}
                  last_page={clientInfo?.last_page}
                  next_page_url={clientInfo?.next_page_url}
                  prev_page_url={clientInfo?.prev_page_url}
                />
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700">
                    Client: {clientInfo?.client_name}
                  </p>
                  <p className="text-sm font-medium text-gray-700">
                    Client ID: {clientInfo?.client_unique_id}
                  </p>
                </div>
              </div>
            ) : selectedClientId ? (
              <p>No information available for this client</p>
            ) : (
              <p>Select a client to view their information</p>
            )}
          </div>
        </div>
      </div>
    </AdminSidebar>
  );
};

export default ClientReport;
